import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ReportKind, sendTranslationReport, trackMeaningReported } from './reportTranslation'
import './reportLink.css'

interface ReportLinkProps {
  kind: ReportKind
  refId: string
  /** The language of the translation being reported (never 'en'). */
  lang: string
}

/**
 * A quiet "Report this meaning" link under a learner-language translation. One tap sends the
 * report and says thanks; the learner can then add a note if they want to.
 */
const ReportLink: React.FC<ReportLinkProps> = ({ kind, refId, lang }) => {
  const { t } = useTranslation('reader')
  const [state, setState] = useState<'idle' | 'sent' | 'note' | 'noteSent'>('idle')
  const [note, setNote] = useState('')

  // A different word or sentence starts over.
  useEffect(() => {
    setState('idle')
    setNote('')
  }, [kind, refId, lang])

  const report = (event: React.MouseEvent) => {
    event.stopPropagation()
    setState('sent')
    trackMeaningReported(kind, lang)
    sendTranslationReport({ kind, ref_id: refId, lang })
  }

  const sendNote = (event: React.FormEvent) => {
    event.preventDefault()
    if (!note.trim()) return
    setState('noteSent')
    sendTranslationReport({ kind, ref_id: refId, lang, note })
  }

  if (state === 'idle') {
    return (
      <button type='button' className='report-link' onClick={report}>
        {kind === 'word' ? t('report.meaning') : t('report.translation')}
      </button>
    )
  }

  return (
    <div className='report-done' role='status'>
      <span>{t('report.thanks')}</span>
      {state === 'sent' && (
        <button type='button' className='report-link' onClick={() => setState('note')}>
          {t('report.addNote')}
        </button>
      )}
      {state === 'note' && (
        <form className='report-note' onSubmit={sendNote}>
          <label className='report-note-label'>
            {t('report.noteLabel')}
            <input
              className='report-note-input'
              value={note}
              maxLength={500}
              placeholder={t('report.notePlaceholder')}
              onChange={(event) => setNote(event.target.value)}
              autoFocus
            />
          </label>
          <button type='submit' className='report-link' disabled={!note.trim()}>
            {t('report.sendNote')}
          </button>
        </form>
      )}
      {state === 'noteSent' && <span>{t('report.noteSent')}</span>}
    </div>
  )
}

export default ReportLink
